import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { UserRole } from '@services/auth/role.enum';
import {
  ApproveESVRInfoUpdate,
  BatchStatus,
  DispatchESVRInfoUpdate,
  DisputeESVRCommand,
  EditESVRCommand,
  ESVRStatus,
  ESVRViewModel,
  RejectESVRCommand,
  VehicleType
} from '@services/cleanfill-api/cleanfill-api.generated';
import { StoreAppService } from '@services/cleanfill-core/store-app/store-app.service';
import { StoreFillSitesService } from '@services/cleanfill-core/store-fill-sites/store-fill-sites.service';
import { StoreSourceSitesService } from '@services/cleanfill-core/store-source-sites/store-source-sites.service';
import { AuthService } from '@services/core/auth.service';
import { noWhitespaceValidator } from '@validators/sync/white-space.validator';
import { PortalState } from '@view-models/enums/portal-state';
import { EditableESVRElement } from '@view-models/interfaces/editable-esvr-element.interface';
import { SearchQuery } from '@view-models/search-query/search-query';
import { SidenavService } from '@widgets/site-common/navbar/side-nav/side-nav.service';
import { SnackbarService } from '@widgets/site-common/snackbar/snackbar.service';
import { AddressPipePipe } from '@widgets/ui-common/pipes/address-pipe/address-pipe.pipe';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { BatchDetailStore } from '../batch-detail.store';
import { ContactInfo, LoadInfo, UserFullName } from './editable-esvr.interface';
import { EsvrTransportInformationComponent } from './esvr-transport-information/esvr-transport-information.component';

enum PopulateFields {
  Hauler = 'Hauler',
  TruckNumber = 'TruckNumber',
  VehicleType = 'VehicleType',
  VehiclePlate = 'VehiclePlate',
  Driver = 'Driver',
  VolumeLoad = 'VolumeLoad',
  VehicleWeight = 'VehicleWeight',
  TareWeight = 'TareWeight',
  GrossWeight = 'GrossWeight',
  ExcavationLocation = 'ExcavationLocation',
  PlacementLocation = 'PlacementLocation',
  CustomerBOL = 'CustomerBOL',
}

enum ActionType {
  Dispatch = 'Dispatch',
  Accept = 'Accept',
}

@Component({
  selector: 'app-editable-esvr',
  templateUrl: './editable-esvr.component.html',
  styleUrls: ['./editable-esvr.component.scss'],
  providers: [AddressPipePipe],
})
export class EditableEsvrComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();
  esvr$: Observable<ESVRViewModel>;
  activeUser$: Observable<UserFullName>;
  editableElementsSubject$ = new BehaviorSubject<EditableESVRElement>(null);
  editableElements$ = this.editableElementsSubject$.asObservable();

  UserRole = UserRole;
  ActionType = ActionType;
  PortalState = PortalState;
  portalState: PortalState;
  loadInfo: LoadInfo;
  contactInfo: ContactInfo;
  form: FormGroup;
  isFillSiteUser: boolean;
  isSourceSiteUser: boolean;
  isReceived: boolean;
  isRejected: boolean;
  isArchived: boolean;
  showActionButtons: boolean;
  accessToActions: boolean;
  submited = false;
  editing = false;

  @ViewChild('triggerDispatch') triggerDispatch: MatMenuTrigger;
  @ViewChild('triggerAccept') triggerAccept: MatMenuTrigger;
  @ViewChild('triggerDispute') triggerDispute: MatMenuTrigger;
  @ViewChild(EsvrTransportInformationComponent) tranportComponent: EsvrTransportInformationComponent;
  constructor(
    private fb: FormBuilder,
    public appService: StoreAppService,
    private sourceSiteStore: StoreSourceSitesService,
    private fillSiteStore: StoreFillSitesService,
    private batchDetailStore: BatchDetailStore,
    private sidenavService: SidenavService,
    private snackbar: SnackbarService,
    private addressPipePipe: AddressPipePipe,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.subscriptions.add(
      this.batchDetailStore.esvrId$.subscribe(async () => {
        this.portalState = await this.appService.portalState$.pipe(take(1)).toPromise();
        this.isFillSiteUser = this.portalState === PortalState.FILL_SITE;
        this.isSourceSiteUser = this.portalState === PortalState.SOURCE_SITE;
        this.submited = false;
        this.editing = false;
        this.loadInfo = null;
        this.contactInfo = null;
        this.initForm();
        this.initDataSources();
      })
    );

    this.subscriptions.add(
      this.batchDetailStore.batch$.subscribe((b) => {
        this.isArchived = b.status && b.status === BatchStatus.Archive;
      })
    );
  }

  initDataSources(): void {
    this.esvr$ = this.batchDetailStore.esvr$;
    this.activeUser$ = this.authService.activeUser$.pipe(
      map((user) => {
        return { fullName: user?.firstName + ' ' + user?.lastName } as UserFullName;
      })
    );
    if (this.batchDetailStore.esvrId) {
      this.batchDetailStore.loadESVR(this.batchDetailStore.esvrId, (esvr) => {
        if (esvr) {
          this.batchDetailStore.init(esvr.batchUniqueId);

          this.accessToActions = this.isFillSiteUser ? esvr.userHasAccessToESVRFillSite : esvr.userHasAccessToESVRSourceSite;
          this.loadInfo = {
            bol: esvr.esvrNumber ?? '',
            batchId: esvr.batchName ?? '',
            status: esvr.status ?? '',
            sourceSiteUniqueId: esvr.sourceSite?.uniqueId,
            sourceSiteAddress: this.addressPipePipe.transform(esvr.sourceSite?.address),
            fillSiteUniqueId: esvr.fillSite?.uniqueId,
            fillSiteAddress: this.addressPipePipe.transform(esvr.fillSite?.address),
          };
          this.contactInfo = {
            projectLeader: esvr.sourceSite?.contact,
            sourceSiteRep: esvr.sourceSiteRepresentative?.fullName,
            fillSiteRep: esvr.fillSiteRepresentative?.fullName
          };
          this.isReceived = esvr.status === ESVRStatus.Received;
          this.isRejected = esvr.status === ESVRStatus.Rejected;
          this.loadAssignedUser(esvr.sourceSite?.uniqueId, esvr.fillSite?.uniqueId);

          // this.updateEditableElements(esvr.status);
          this.patchForm(esvr);
          this.form.markAsUntouched();
          if (!this.accessToActions) {
            this.form.disable();
          } else {
            if (this.isFillSiteUser && esvr.status === ESVRStatus.Prepared) {
              this.showActionButtons = false;
              this.form.disable();
            } else {
              if (this.isSourceSiteUser) {
                this.showActionButtons = esvr.status === ESVRStatus.Prepared;
              } else {
                this.showActionButtons = [ESVRStatus.Dispatched, ESVRStatus.PendingApproval].includes(esvr.status);
              }
              this.form.enable();
            }
          }

          this.tranportComponent.repopulate();

          this.subscriptions.add(
            this.form
              .get('esvrTransportInformationForm')
              .get('vehicleType')
              .valueChanges.subscribe((value) => {
                if (value === VehicleType.Triaxle) {
                  this.form
                    .get('esvrWeighPlacementLocationForm')
                    .get('volumeLoad')
                    .setValue(20);
                } else {
                  this.form
                    .get('esvrWeighPlacementLocationForm')
                    .get('volumeLoad')
                    .setValue(37);
                }
              }));
        }
      });
    }
  }

  loadAssignedUser(sourceSiteUniqueId: string, fillSiteUniqueId: string) {
    const query = new SearchQuery();
    if (sourceSiteUniqueId && this.isSourceSiteUser) {
      this.sourceSiteStore.loadUsers(sourceSiteUniqueId, query);
    }
    if (fillSiteUniqueId && this.isFillSiteUser) {
      this.fillSiteStore.loadUsers(fillSiteUniqueId, query);
    }
  }

  async isModificationAllowed() {
    const esvr = (await this.esvr$.pipe(filter(Boolean), take(1)).toPromise()) as ESVRViewModel;
    if (!esvr?.outbound) {
      return false;
    }
    const now = moment();
    const duration = moment.duration(now.diff(esvr?.outbound));
    const hours = duration.asHours();
    if (hours >= 48) {
      return false;
    }
    return true;
  }

  getValidation(validators: Validators[], field: PopulateFields) {
    const requiredSource = [PopulateFields.ExcavationLocation, PopulateFields.VolumeLoad];
    const requiredReceiving = [PopulateFields.PlacementLocation, PopulateFields.VolumeLoad];
    if ((this.isSourceSiteUser && requiredSource.includes(field)) || (this.isFillSiteUser && requiredReceiving.includes(field))) {
      return validators.concat([Validators.required]);
    } else {
      return validators;
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      esvrTransportInformationForm: this.fb.group({
        hauler: [
          { value: '', disabled: !(this.isSourceSiteUser || this.isFillSiteUser) },
          this.getValidation([Validators.maxLength(100), noWhitespaceValidator], PopulateFields.Hauler),
        ],
        truckNumber: [
          { value: '', disabled: !(this.isSourceSiteUser || this.isFillSiteUser) },
          this.getValidation([Validators.maxLength(15), noWhitespaceValidator], PopulateFields.TruckNumber),
        ],
        vehicleWeight: [
          0, this.getValidation(
            [Validators.pattern(/^\d+(.[0-9]{0,2})?$/), Validators.max(999999), Validators.min(0)],
            PopulateFields.VehicleWeight),
        ],
        vehicleType: [
          { value: '', disabled: !(this.isSourceSiteUser || this.isFillSiteUser) },
          this.getValidation([], PopulateFields.VehicleType),
        ],
        vehiclePlate: [
          { value: '', disabled: !(this.isSourceSiteUser || this.isFillSiteUser) },
          this.getValidation([Validators.maxLength(15), noWhitespaceValidator], PopulateFields.VehiclePlate),
        ],
        driver: ['', this.getValidation([Validators.maxLength(60), noWhitespaceValidator], PopulateFields.Driver)],
      }),
      esvrDateTimeInformationForm: this.fb.group({
        dispatchTime: [''],
        completedTime: [''],
      }),
      esvrWeighPlacementLocationForm: this.fb.group({
        volumeLoad: [
          '',
          this.getValidation(
            [Validators.pattern(/^\d+(.[0-9]{0,2})?$/), Validators.max(100000), Validators.min(0.01)],
            PopulateFields.VolumeLoad
          ),
        ],
        tareWeight: [
          '', this.getValidation(
            [Validators.pattern(/^\d+(.[0-9]{0,2})?$/), Validators.max(999999), Validators.min(0)],
            PopulateFields.TareWeight
          ),
        ],
        grossWeight: [
          '',
          this.getValidation(
            [Validators.pattern(/^\d+(.[0-9]{0,2})?$/), Validators.max(999999), Validators.min(0.01)],
            PopulateFields.GrossWeight
          ),
        ],
        excavationLocation: [''],
        placementLocation: [''],
        customerBOL: [''],
      }),
      esvrSiteContactsForm: this.fb.group({
        sourceSiteRepresentativeUniqueId: [null],
        fillSiteRepresentativeUniqueId: [null],
        comments: ['', [Validators.maxLength(500)]],
      }),
    });

    this.form.markAsPristine();
  }

  patchForm(esvr: ESVRViewModel): void {
    this.form.patchValue({
      esvrTransportInformationForm: {
        hauler: esvr?.hauler ?? '',
        truckNumber: esvr?.truckNumber ?? '',
        vehicleType: esvr?.vehicleType ?? undefined,
        vehiclePlate: esvr?.vehiclePlate ?? '',
        driver: esvr?.driverName ?? '',
      },
      esvrDateTimeInformationForm: {
        dispatchTime: esvr?.outbound ?? '',
        completedTime: esvr?.transferDate ?? '',
      },
      esvrWeighPlacementLocationForm: {
        volumeLoad: esvr?.loadWeight && esvr?.loadWeight !== 0 ? esvr?.loadWeight : undefined,
        excavationLocation: esvr?.excavationLocation ?? '',
        placementLocation: esvr?.placementLocation ?? '',
        customerBOL: esvr?.customerBOL ?? '',
        tareWeight: esvr?.tareWeight ?? 0, // assign value to TareWeight
        grossWeight: esvr?.grossWeight ?? 0,
      },
      esvrSiteContactsForm: {
        sourceSiteRepresentativeUniqueId: esvr?.sourceSiteRepresentative?.uniqueId,
        fillSiteRepresentativeUniqueId: esvr?.fillSiteRepresentative?.uniqueId,
        comments: esvr?.comments ?? '',
      },
    });
    this.form.markAsPristine();
  }

  isValid(actionType: ActionType) {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      this.submited = true;
      return;
    } else {
      this.submited = false;
    }
    if (actionType === ActionType.Dispatch) {
      this.triggerDispatch.openMenu();
    }
    if (actionType === ActionType.Accept) {
      this.acceptESVR(true);
    }
  }

  openDisputeMenu() {
    this.triggerDispute.openMenu();
  }

  dispatchESVR($event: boolean) {
    if (!$event) {
      this.triggerDispatch.closeMenu();
      return;
    }

    if (this.form.valid) {
      const { driver, hauler, truckNumber, vehiclePlate, vehicleType, vehicleWeight } =
        this.form.get('esvrTransportInformationForm').value;
      const { excavationLocation, customerBOL, volumeLoad, tareWeight, grossWeight } =
        this.form.get('esvrWeighPlacementLocationForm').value;
      const haulerName = hauler?.name || hauler;
      const dispatchESVRInfoUpdateObject = {
        driverName: driver,
        hauler: haulerName,
        truckNumber,
        vehiclePlate,
        vehicleType,
        excavationLocation,
        customerBOL,
        loadWeight: volumeLoad,
        tareWeight,
        grossWeight,
        vehicleWeight
      };
      this.batchDetailStore.dispatchESVR(this.batchDetailStore.esvrId, new DispatchESVRInfoUpdate(dispatchESVRInfoUpdateObject), () => {
        this.triggerDispatch.closeMenu();
        this.snackbar.success('ESVR Dispatch  Successfully');
        this.closeSidenav();
      });
    }
  }

  async updateEditableElements(status: ESVRStatus) {
    let editableElements = {
      placementLocation: false,
      excavationLocation: false,
      customerBOL: false,
      loadWeight: false,
      hauler: false,
      driverName: false,
      truckNumber: false,
      tareWeight: false,
      vehicleWeight: false,
      grossWeight: false,
      vehiclePlate: false,
      vehicleType: false,
      comments: false,
    };

    if (this.isSourceSiteUser) {
      if ([ESVRStatus.Dispatched, ESVRStatus.Rejected, ESVRStatus.PendingApproval, ESVRStatus.Received].includes(status)) {
        const allowed = await this.isModificationAllowed();
        if (allowed) {
          editableElements = {
            ...editableElements,
            excavationLocation: true,
            customerBOL: true,
            driverName: true,
            hauler: true,
            truckNumber: true,
            tareWeight: true,
            vehicleWeight: true,
            grossWeight: true,
            vehiclePlate: true,
            vehicleType: true,
          };
        }
      } else if (status === ESVRStatus.Prepared) {
        editableElements = {
          ...editableElements,
          excavationLocation: true,
          customerBOL: true,
          loadWeight: true,
          driverName: true,
          hauler: true,
          truckNumber: true,
          vehicleWeight: true,
          tareWeight: true,
          grossWeight: true,
          vehiclePlate: true,
          vehicleType: true,
        };
      }
    }
    if (this.isFillSiteUser) {
      if ([ESVRStatus.Rejected, ESVRStatus.PendingApproval, ESVRStatus.Received].includes(status)) {
        editableElements = { ...editableElements, placementLocation: true, loadWeight: true, comments: true };
      } else if (status === ESVRStatus.Dispatched) {
        editableElements = { ...editableElements, placementLocation: true, loadWeight: true };
      }
    }
    this.editableElementsSubject$.next(editableElements);
  }

  editESVR() {
    this.editing = true;
  }

  saveChanges() {
    if (!this.isValidForSave()) {
      return;
    }

    const esvrInfoUpdateObject = this.getESVRUpdateObject();
    this.batchDetailStore.saveESVR(this.batchDetailStore.esvrId, new EditESVRCommand(esvrInfoUpdateObject), () => {
      this.snackbar.success('ESVR Updated Successfully');
      this.closeSidenav();
    });
  }

  getESVRUpdateObject() {
    const { hauler, vehicleType, truckNumber, vehiclePlate, driver, vehicleWeight } = this.form.get('esvrTransportInformationForm').value;
    const { dispatchTime, completedTime } = this.form.get('esvrDateTimeInformationForm').value;
    const { volumeLoad, tareWeight, grossWeight, placementLocation, excavationLocation, customerBOL } =
      this.form.get('esvrWeighPlacementLocationForm').value;
    const { sourceSiteRepresentativeUniqueId, fillSiteRepresentativeUniqueId, comments } = this.form.get('esvrSiteContactsForm').value;
    const esvrUpdateObject = {
      uniqueId: this.batchDetailStore.esvrId,
      hauler: hauler?.name ?? hauler,
      vehicleType,
      vehiclePlate,
      truckNumber,
      driverName: driver,
      outbound: dispatchTime,
      transferDate: completedTime,
      loadWeight: volumeLoad,
      tareWeight, // don't change for manual etnry
      vehicleWeight,
      grossWeight,
      placementLocation,
      excavationLocation,
      customerBOL,
      sourceSiteRepresentativeUniqueId,
      fillSiteRepresentativeUniqueId,
      comments,
    };
    return esvrUpdateObject;
  }

  isValidForSave(): boolean {
    const formTransport = this.form.get('esvrTransportInformationForm') as FormGroup;
    const formWeight = this.form.get('esvrWeighPlacementLocationForm') as FormGroup;
    let isValid = true;

    if (this.isSourceSiteUser) {
      Object.keys(formTransport.controls).forEach((key) => {
        const controlErrors: ValidationErrors = formTransport.get(key).errors;
        if (controlErrors !== null && !controlErrors.required) {
          isValid = false;
        }
      });
    }

    if (this.isSourceSiteUser || this.isFillSiteUser) {
      Object.keys(formWeight.controls).forEach((key) => {
        const controlErrors: ValidationErrors = formWeight.get(key).errors;
        if (controlErrors !== null && !controlErrors.required) {
          isValid = false;
        }
      });
    }
    return isValid;
  }

  acceptESVR($event: boolean) {
    if (!$event) {
      this.triggerAccept.closeMenu();
      return;
    }
    if (this.form.valid) {
      const { placementLocation, volumeLoad, tareWeight, grossWeight, netWeight } = this.form.get('esvrWeighPlacementLocationForm').value;

      const acceptESVRInfoUpdateObject = {
        placementLocation,
        loadWeight: volumeLoad,
        tareWeight,
        grossWeight
      };

      this.batchDetailStore.acceptESVR(this.batchDetailStore.esvrId, new ApproveESVRInfoUpdate(acceptESVRInfoUpdateObject), () => {
        this.batchDetailStore.refresh().then(() => {
          this.snackbar.success('ESVR Approved Successfully');
          this.triggerAccept.closeMenu();
          this.closeSidenav();
        });
      });
    }
  }

  CloseDisputeESVR() {
    this.triggerDispute.closeMenu();
    this.submited = false;
  }

  rejectESVR(reason: string) {
    if (reason) {
      this.batchDetailStore.rejectESVR(this.batchDetailStore.esvrId, new RejectESVRCommand({ comments: reason }), () => {
        this.snackbar.success('ESVR rejected Successfully');
        this.triggerDispute.closeMenu();
        this.closeSidenav();
      });
    }
  }

  holdESVR(reason: string) {
    this.batchDetailStore.holdESVR(this.batchDetailStore.esvrId, new DisputeESVRCommand({ comments: reason }), () => {
      this.snackbar.success('ESVR put On-Hold Successfully');
      this.triggerDispute.closeMenu();
      this.closeSidenav();
    });
  }

  closeSidenav() {
    this.showActionButtons = false;
    this.sidenavService.close();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
